class LocalizationDropdown {
  constructor(root, picker) {
    this.root = root
    this.picker = picker
    this.trigger = root.querySelector(".localization-picker__dropdown-trigger")
    this.menu = root.querySelector(".localization-picker__dropdown-menu")
    this.label = root.querySelector("[data-dropdown-label]")
    this.input = this.getFormInput()
    this.flag = root.querySelector("[data-dropdown-flag]")
    this.searchInput = root.querySelector("[data-dropdown-search]")
    this.options = Array.from(root.querySelectorAll(".localization-picker__option-btn"))

    this.handleTriggerClick = this.handleTriggerClick.bind(this)
    this.handleOptionClick = this.handleOptionClick.bind(this)
    this.handleSearchInput = this.handleSearchInput.bind(this)
  }

  getFormInput() {
    const inputKey = this.root.dataset.dropdownType === "language" ? "language" : "country"
    return this.picker.form?.querySelector(`[data-localization-input="${inputKey}"]`)
  }

  connectedCallback() {
    if (!this.trigger || !this.menu) return

    this.trigger.addEventListener("click", this.handleTriggerClick)

    this.options.forEach((option) => {
      option.addEventListener("click", this.handleOptionClick)
    })

    if (this.searchInput) {
      this.searchInput.addEventListener("input", this.handleSearchInput)
    }
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener("click", this.handleTriggerClick)
    this.options.forEach((option) => {
      option.removeEventListener("click", this.handleOptionClick)
    })
    this.searchInput?.removeEventListener("input", this.handleSearchInput)
  }

  handleTriggerClick(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.menu.hasAttribute("hidden")) {
      this.picker.closeAllDropdowns(this)
      this.open()
      return
    }

    this.close()
  }

  open() {
    this.menu.removeAttribute("hidden")
    this.trigger.setAttribute("aria-expanded", "true")
    this.root.classList.add("is-open")
    this.searchInput?.focus()
  }

  close() {
    this.menu.setAttribute("hidden", "")
    this.trigger.setAttribute("aria-expanded", "false")
    this.root.classList.remove("is-open")

    if (this.searchInput) {
      this.searchInput.value = ""
      this.filterOptions("")
    }
  }

  handleOptionClick(event) {
    const option = event.currentTarget
    const value = option.dataset.value
    const label = option.dataset.label

    if (!value || !this.input) return

    this.input.value = value

    if (this.label) {
      this.label.textContent = label || option.textContent.trim()
    }

    if (this.flag && option.dataset.flagClass) {
      this.flag.className = `localization-picker__flag country-flags ${option.dataset.flagClass}`
    }

    this.options.forEach((item) => {
      const isSelected = item === option
      item.classList.toggle("is-selected", isSelected)
      item.setAttribute("aria-selected", isSelected ? "true" : "false")
    })

    this.close()
  }

  handleSearchInput(event) {
    this.filterOptions(event.currentTarget.value.trim().toLowerCase())
  }

  filterOptions(query) {
    this.options.forEach((option) => {
      const listItem = option.closest(".localization-picker__option")
      const text = option.textContent.trim().toLowerCase()
      listItem?.classList.toggle("is-hidden", query !== "" && !text.includes(query))
    })
  }
}

class LocalizationPicker extends HTMLElement {
  static MOBILE_QUERY = "(max-width: 1024.98px)"
  static openPickerCount = 0

  constructor() {
    super()
    this.handleDocumentClick = this.handleDocumentClick.bind(this)
    this.handleKeyUp = this.handleKeyUp.bind(this)
    this.handleBackdropClick = this.handleBackdropClick.bind(this)
    this.handleTouchMove = this.handleTouchMove.bind(this)
    this.handleApplyClick = this.handleApplyClick.bind(this)
    this.dropdowns = []
    this.panelPlaceholder = null
    this.backdrop = null
    this.addedOpenModal = false
  }

  connectedCallback() {
    this.trigger = this.querySelector(".localization-picker__trigger")
    this.panel = this.querySelector(".localization-picker__panel")
    this.form = this.querySelector(".localization-picker__form")
    this.applyButton = this.querySelector(".localization-picker__apply")

    if (!this.trigger || !this.panel) return

    this.trigger.addEventListener("click", this.handleTriggerClick.bind(this))
    this.applyButton?.addEventListener("click", this.handleApplyClick)
    document.addEventListener("keyup", this.handleKeyUp)

    this.querySelectorAll("[data-localization-dropdown]").forEach((dropdownRoot) => {
      const dropdown = new LocalizationDropdown(dropdownRoot, this)
      dropdown.connectedCallback()
      this.dropdowns.push(dropdown)
    })
  }

  disconnectedCallback() {
    document.removeEventListener("keyup", this.handleKeyUp)
    this.applyButton?.removeEventListener("click", this.handleApplyClick)
    this.close()
    this.restoreMobilePanel()
    this.dropdowns.forEach((dropdown) => dropdown.disconnectedCallback())
    this.dropdowns = []
  }

  isMobile() {
    return window.matchMedia(LocalizationPicker.MOBILE_QUERY).matches
  }

  mountMobilePanel() {
    if (!this.isMobile() || this.panel.parentElement === document.body) return

    this.panelPlaceholder = document.createComment("localization-picker-panel")
    this.panel.before(this.panelPlaceholder)

    this.backdrop = document.createElement("button")
    this.backdrop.type = "button"
    this.backdrop.className = "localization-picker__backdrop btn-reset"
    this.backdrop.setAttribute("aria-label", "Close")
    this.backdrop.addEventListener("click", this.handleBackdropClick)

    document.body.appendChild(this.backdrop)
    document.body.appendChild(this.panel)
    this.panel.classList.add("is-mobile")
  }

  restoreMobilePanel() {
    if (!this.panel || !this.panelPlaceholder) return

    this.panel.classList.remove("is-mobile")
    this.panelPlaceholder.replaceWith(this.panel)
    this.panelPlaceholder = null
    this.backdrop?.remove()
    this.backdrop = null
  }

  lockPageScroll() {
    if (!this.isMobile()) return

    LocalizationPicker.openPickerCount += 1
    if (LocalizationPicker.openPickerCount > 1) return

    const root = document.documentElement

    if (!root.classList.contains("open-modal")) {
      this.addedOpenModal = true
      root.classList.add("open-modal")
    }

    root.classList.add("open-modal-localization", "localization-picker-open")
    document.body.classList.add("localization-picker-open")
    document.addEventListener("touchmove", this.handleTouchMove, { passive: false })
  }

  unlockPageScroll() {
    if (!this.isMobile()) return

    LocalizationPicker.openPickerCount = Math.max(0, LocalizationPicker.openPickerCount - 1)
    if (LocalizationPicker.openPickerCount > 0) return

    const root = document.documentElement

    root.classList.remove("open-modal-localization", "localization-picker-open")
    document.body.classList.remove("localization-picker-open")
    document.removeEventListener("touchmove", this.handleTouchMove)

    if (this.addedOpenModal) {
      root.classList.remove("open-modal")
      this.addedOpenModal = false
    }
  }

  handleTouchMove(event) {
    if (this.panel?.hasAttribute("hidden")) return
    if (this.panel?.contains(event.target)) return
    event.preventDefault()
  }

  handleBackdropClick() {
    this.close()
  }

  handleApplyClick(event) {
    if (!this.isMobile() || !this.form) return

    event.preventDefault()
    event.stopPropagation()

    if (typeof this.form.requestSubmit === "function") {
      this.form.requestSubmit()
      return
    }

    this.form.submit()
  }

  handleTriggerClick(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.panel.hasAttribute("hidden")) {
      this.open()
      return
    }

    this.close()
  }

  open() {
    document.querySelectorAll("localization-picker").forEach((picker) => {
      if (picker !== this) {
        picker.close()
      }
    })

    this.mountMobilePanel()
    this.panel.removeAttribute("hidden")
    this.trigger.setAttribute("aria-expanded", "true")
    this.trigger.classList.add("is-open")
    this.lockPageScroll()
    document.addEventListener("click", this.handleDocumentClick)
  }

  close() {
    if (this.panel.hasAttribute("hidden")) return

    this.panel.setAttribute("hidden", "")
    this.trigger.setAttribute("aria-expanded", "false")
    this.trigger.classList.remove("is-open")
    this.closeAllDropdowns()
    this.restoreMobilePanel()
    this.unlockPageScroll()
    document.removeEventListener("click", this.handleDocumentClick)
  }

  closeAllDropdowns(exceptDropdown) {
    this.dropdowns.forEach((dropdown) => {
      if (dropdown !== exceptDropdown) {
        dropdown.close()
      }
    })
  }

  handleDocumentClick(event) {
    if (event.target === this.backdrop) return

    if (!this.contains(event.target) && !this.panel.contains(event.target)) {
      this.close()
      return
    }

    const clickedDropdown = event.target.closest("[data-localization-dropdown]")

    this.dropdowns.forEach((dropdown) => {
      if (clickedDropdown !== dropdown.root) {
        dropdown.close()
      }
    })
  }

  handleKeyUp(event) {
    if (event.code !== "Escape" || this.panel.hasAttribute("hidden")) return
    this.close()
    this.trigger.focus()
  }
}

customElements.define("localization-picker", LocalizationPicker)
